/**
 * Pincode Service
 * Fetches location options for a 6-digit Indian pincode via OpenStreetMap Nominatim.
 *
 * @author Varun Pandey
 */

export interface PostOffice {
  Name: string;
  Description: string | null;
  BranchType: string;
  DeliveryStatus: string;
  Circle: string;
  District: string;
  Division: string;
  Region: string;
  Block: string;
  State: string;
  Country: string;
  Pincode: string;
}

type NominatimSearchResult = {
  address?: Record<string, string>;
};

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_USER_AGENT = 'starclinch-app';

const EMPTY_POST_OFFICE_FIELDS = {
  Description: null as string | null,
  BranchType: '',
  DeliveryStatus: '',
  Circle: '',
  Division: '',
  Region: '',
  Block: '',
};

function norm(s: string): string {
  return String(s ?? '').trim().toLowerCase();
}

function isDelhiPlaceName(name: string): boolean {
  const n = norm(name);
  return n === 'delhi' || n === 'new delhi' || n.endsWith(' delhi');
}

/** Infer Indian state/UT label from city or district when Nominatim omits state. */
export function inferIndianStateFromPlace(city?: string, district?: string): string {
  for (const raw of [city, district]) {
    const value = String(raw ?? '').trim();
    if (!value) continue;
    if (isDelhiPlaceName(value)) return 'Delhi';
  }
  return '';
}

/** Nominatim often omits `state` for Delhi (IN-DL); map ISO codes and place names to a state label. */
function resolveStateFromNominatimAddress(addr: Record<string, string>): string {
  const direct = (addr.state || '').trim();
  if (direct) return direct;

  const iso = (addr['ISO3166-2-lvl4'] || '').trim();
  if (iso === 'IN-DL') return 'Delhi';

  for (const field of [addr.city, addr.town, addr.county, addr.state_district, addr.region]) {
    const value = String(field ?? '').trim();
    if (value && isDelhiPlaceName(value)) return 'Delhi';
  }

  return '';
}

function mapNominatimToPostOffice(
  item: NominatimSearchResult,
  fallbackPincode: string
): PostOffice | null {
  const addr = item.address ?? {};
  const district = (
    addr.city ||
    addr.town ||
    addr.village ||
    addr.suburb ||
    addr.county ||
    ''
  ).trim();
  const state =
    resolveStateFromNominatimAddress(addr) ||
    inferIndianStateFromPlace(district);
  const name = (
    addr.suburb ||
    addr.neighbourhood ||
    addr.village ||
    addr.town ||
    addr.city ||
    ''
  ).trim();

  if (!district && !state) return null;

  return {
    Name: name || district,
    District: district,
    State: state,
    Pincode: (addr.postcode || fallbackPincode).trim(),
    Country: (addr.country || 'India').trim(),
    ...EMPTY_POST_OFFICE_FIELDS,
  };
}

function dedupePostOffices(list: PostOffice[]): PostOffice[] {
  const seen = new Set<string>();
  const out: PostOffice[] = [];
  for (const po of list) {
    const key = `${norm(po.Name)}|${norm(po.District)}|${norm(po.State)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(po);
  }
  return out;
}

export class PincodeService {
  /**
   * Get location options for a given pincode via Nominatim (direct fetch, no auth API).
   * @param pincode 6-digit pincode
   * @returns List of location options or empty list if none found
   */
  static async getDetails(pincode: string): Promise<PostOffice[]> {
    if (!pincode || pincode.length !== 6) {
      return [];
    }

    try {
      const params = new URLSearchParams({
        postalcode: pincode,
        countrycodes: 'in',
        format: 'jsonv2',
        addressdetails: '1',
      });

      const res = await fetch(`${NOMINATIM_SEARCH_URL}?${params}`, {
        headers: { 'User-Agent': NOMINATIM_USER_AGENT },
      });

      if (!res.ok) return [];

      const data = await res.json();
      const results = Array.isArray(data) ? (data as NominatimSearchResult[]) : [];

      const mapped = results
        .map((item) => mapNominatimToPostOffice(item, pincode))
        .filter((po): po is PostOffice => po != null);

      return dedupePostOffices(mapped);
    } catch (error) {
      console.error('Error fetching pincode details:', error);
      return [];
    }
  }
}
